import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useRevalidator } from "react-router-dom";
import { toast } from "react-toastify";
import MuiAutocomplete from "../components/autocomplete/MuiAutocomplete";
import { Profession } from "../professions/type";
import { changeUserProfession, getUserProfessionInfo } from "./api";
import { UserProfessionInfo } from "./type";

const errorsOf = (e: any) =>
  (e?.response?.data?.errors as string[] | undefined)?.join(" ") ||
  "İşlem başarısız";

type Props = {
  userId: string;
  professionCode?: string | null;
};

/**
 * Kullanıcı detay sayfasında branş bilgisini gösterir ve yöneticinin
 * onay dialogu ile branşı değiştirmesini sağlar.
 */
export default function ProfessionChangeSection({
  userId,
  professionCode,
}: Props) {
  const revalidator = useRevalidator();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<UserProfessionInfo | null>(null);
  const [selected, setSelected] = useState<Profession | null>(null);

  const openDialog = async () => {
    setOpen(true);
    setSelected(null);
    setInfo(null);
    setLoading(true);
    try {
      const res = await getUserProfessionInfo(userId);
      if (res.data.isSuccess) setInfo(res.data.data);
      else toast.error(res.data.errors?.[0] || "Branş bilgisi alınamadı");
    } catch (e) {
      toast.error(errorsOf(e));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (!busy) setOpen(false);
  };

  const sameProfession =
    !!selected &&
    selected.professionCode === (info?.professionCode ?? professionCode);
  const blocked = !!info?.hasActivePeriodPackage;
  const canSubmit =
    !!selected && !sameProfession && !blocked && !loading && !busy;

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await changeUserProfession(userId, selected.professionCode);
      if (res.data.isSuccess) {
        toast.success(
          `Branş ${res.data.data?.professionName ?? selected.name} olarak güncellendi`,
        );
        setOpen(false);
        revalidator.revalidate();
      } else {
        toast.error(res.data.errors?.[0] || "Branş güncellenemedi");
      }
    } catch (e) {
      toast.error(errorsOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{ mt: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            Branş
          </Typography>
          <Typography variant="body1">{professionCode || "-"}</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          onClick={openDialog}
        >
          Branş Değiştir
        </Button>
      </Stack>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Branş Değiştir</DialogTitle>
        <DialogContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Mevcut branş:{" "}
              <strong>
                {info?.professionName ?? "-"}
                {info?.professionCode ? ` (${info.professionCode})` : ""}
              </strong>
            </Typography>

            {blocked && (
              <Alert severity="error">
                Kullanıcının mevcut branşın aktif atama dönemine (
                {info?.activeAssignmentPeriodCode}) ait premium paketi var.
                Branş değiştirilemez.
              </Alert>
            )}
            {!blocked && (info?.activePeriodPreferenceCount ?? 0) > 0 && (
              <Alert severity="warning">
                Kullanıcının aktif dönemde ({info?.activeAssignmentPeriodCode})
                kayıtlı {info?.activePeriodPreferenceCount} tercihi var. Branş
                değişince bu tercihler eski branşın dönemine bağlı kalır ve
                kullanıcı yeni branşta tercihlerini yeniden girmelidir.
              </Alert>
            )}

            <MuiAutocomplete<Profession>
              url="/api/professions"
              dataItemKey="professionCode"
              dataTextValue="name"
              label="Yeni Branş"
              fullWidth
              disabled={blocked || loading}
              onChange={(_e, value) => setSelected(value as Profession | null)}
            />
            {sameProfession && (
              <Alert severity="info">Kullanıcı zaten bu branşta.</Alert>
            )}

            <Alert severity="info">
              Değişiklik, kullanıcının oturumu yenilendiğinde (token refresh
              veya yeniden giriş) geçerli olur.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Vazgeç
          </Button>
          <Button variant="contained" onClick={submit} disabled={!canSubmit}>
            {busy ? "Değiştiriliyor..." : "Değiştir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
